/*
 * RE:SELECT ヘアケア診断 — 点数計算（2026-10-23）
 * 元：REMENTE AI診断 Phase1（テーマ 189327376696）の assets/remente-ai-recommender.js
 *
 * - 外部AIは使わない。回答と、商品に付いた ai2_ タグ（商品説明だけを根拠に付与）を照合して点数を付ける。
 * - 商品データは表示時にストアから作る（templates/collection.hair-check-data.liquid）。
 * - 商品の効果を断定する文言は出さない。理由は「商品説明にどう登録されているか」だけを示す。
 * - 1つの軸に複数の値があるときは、どれか1つ一致すれば加点する。
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  root.REMENTE_HAIR_CHECK = api;
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";

  /* ---------- 質問の回答 → ai2_ タグの値 ---------- */

  // 最優先の悩み → ai2_concern の値
  const CONCERN_TAGS = {
    dryness: ["乾燥", "パサつき", "乾燥・パサつき", "ごわつき"],
    damage: ["ダメージ", "枝毛", "切れ毛", "熱ダメージ", "絡まり"],
    wave: ["うねり", "広がり・うねり", "湿気"],
    frizz: ["広がり", "広がり・うねり", "湿気", "静電気", "アホ毛・パヤ毛"],
    shine: ["ツヤ不足"],
    texture: ["ごわつき", "絡まり", "パサつき", "乾燥・パサつき"],
    color_fade: ["カラー褪色", "褪色", "黄ばみ", "黄ばみ（ブリーチ）"],
    low_volume: ["ボリューム不足", "ぺたんこ", "ハリコシ不足", "やせ髪"],
    high_volume: ["広がり", "広がり・うねり"],
    aging: ["エイジング", "白髪・エイジング", "ハリコシ不足", "やせ髪"],
    scalp: ["頭皮のベタつき", "頭皮の乾燥", "抜け毛", "薄毛"],
  };

  // ai2_concern 以外の軸でも、その悩み向けとして登録されているとみなすもの
  function concernExtras(p, key) {
    const t = p.t;
    switch (key) {
      case "wave": return any(t.curl, ["うねり", "くせ毛", "強いくせ", "伸ばしたい"]);
      case "low_volume": return any(t.vol, ["出したい"]);
      case "high_volume": return any(t.vol, ["多い"]);
      case "aging": return any(t.for, ["エイジング世代"]);
      case "shine": return any(t.finish, ["ツヤ"]);
      case "damage": return has(t.dmg);
      case "color_fade": return any(t.treat, ["カラー", "ハイトーンカラー"]) || has(t.tone);
      case "scalp": return has(t.scalp) || p.categories.includes("scalp_care");
      default: return false;
    }
  }

  const RELATED_CONCERNS = {
    dryness: ["texture", "shine"],
    damage: ["color_fade", "texture"],
    wave: ["frizz", "high_volume"],
    frizz: ["wave", "high_volume"],
    shine: ["dryness", "texture"],
    texture: ["dryness", "damage"],
    color_fade: ["damage"],
    low_volume: ["aging"],
    high_volume: ["wave", "frizz"],
    aging: ["low_volume"],
    scalp: [],
  };

  // 「自分に何が必要かわからない」→ 深掘りの回答を悩みへ読み替える
  const EXPLORATION = {
    unknown_0: "dryness",
    unknown_1: "damage",
    unknown_2: "frizz",
    unknown_3: "low_volume",
    unknown_4: "scalp",
    unknown_5: "shine",
  };

  const CONCERN_LABELS = {
    dryness: "乾燥・パサつき",
    damage: "ダメージ・枝毛・切れ毛",
    wave: "くせ・うねり",
    frizz: "広がり",
    shine: "ツヤ",
    texture: "手触り",
    color_fade: "カラーの色持ち",
    low_volume: "ボリューム不足",
    high_volume: "毛量・広がり",
    aging: "ハリ・コシ",
    scalp: "頭皮ケア",
  };

  // 理想の仕上がり → ai2_finish
  const FINISH_TAGS = {
    moist_smooth: ["しっとり", "まとまり", "うるおい"],
    light_silky: ["さらさら", "軽やか", "素髪感"],
    soft_smooth: ["やわらか", "なめらか", "しなやか"],
    glossy: ["ツヤ"],
    airy: ["ふんわり", "ボリューム", "立ち上がり"],
    body: ["ハリコシ", "弾力"],
    natural: ["自然な動き", "素髪感", "動き"],
    styled: ["束感", "ウェット", "セミウェット", "キープ", "ハードキープ", "カールキープ", "ウェーブ", "マット", "動き", "毛先が内に入る"],
  };
  const RELATED_FINISHES = {
    moist_smooth: ["soft_smooth", "natural"],
    light_silky: ["airy", "natural"],
    soft_smooth: ["moist_smooth", "glossy"],
    glossy: ["soft_smooth", "moist_smooth"],
    airy: ["light_silky", "body"],
    body: ["airy"],
    natural: ["light_silky", "soft_smooth", "styled"],
    styled: ["natural"],
  };
  const FINISH_LABELS = {
    moist_smooth: "しっとりまとまる",
    light_silky: "軽くさらさら",
    soft_smooth: "やわらかくなめらか",
    glossy: "ツヤ",
    airy: "ふんわり軽やか",
    body: "ハリ・コシ",
    natural: "自然な仕上がり",
    styled: "束感・動き・キープ",
  };

  // 商品の種類（ai2_type）→ 探しているアイテム
  const TYPE_CATEGORY = {
    "シャンプー": ["shampoo"],
    "クリームシャンプー": ["shampoo"],
    "クレンジングシャンプー": ["shampoo"],
    "カラーシャンプー": ["shampoo"],
    "スカルプシャンプー": ["shampoo", "scalp_care"],
    "トリートメント": ["treatment"],
    "カラートリートメント": ["treatment"],
    "プレトリートメント": ["treatment"],
    "プレシャンプー": ["treatment"],
    "スカルプトリートメント": ["treatment", "scalp_care"],
    "ヘアマスク": ["hair_mask"],
    "洗い流さないトリートメント": ["leave_in"],
    "アウトバス": ["leave_in"],
    "ヘアオイル": ["leave_in"],
    "オイル": ["leave_in"],
    "ヘアミルク": ["leave_in"],
    "ミルク": ["leave_in"],
    "ヘアミスト": ["leave_in"],
    "ミスト": ["leave_in"],
    "ヘアセラム": ["leave_in"],
    "ヘアクリーム": ["leave_in"],
    "ミックスエッセンス": ["leave_in"],
    "ヘアグロス": ["leave_in", "styling"],
    "スタイリング": ["styling"],
    "スタイリング剤": ["styling"],
    "スタイリングバーム": ["styling"],
    "スタイリングミルク": ["styling"],
    "ヘアグリース": ["styling"],
    "ヘアジェル": ["styling"],
    "ヘアジャム": ["styling"],
    "ヘアジュレ": ["styling"],
    "ヘアスプレー": ["styling"],
    "ヘアフォーム": ["styling"],
    "ヘアムース": ["styling"],
    "ヘアワックス": ["styling"],
    "ポイントスティック": ["styling"],
    "バーム": ["styling", "leave_in"],
    "ヘアバーム": ["styling", "leave_in"],
    "ドライシャンプー": ["scalp_care"],
    "スカルプ": ["scalp_care"],
    "スカルプケア": ["scalp_care"],
    "スカルプエッセンス": ["scalp_care"],
    "スカルプオイル": ["scalp_care"],
    "頭皮クレンジング": ["scalp_care"],
    "頭皮スクラブ": ["scalp_care"],
    "ドライヤー": ["device"],
    "ストレートアイロン": ["device"],
    "カールアイロン": ["device"],
    "コームアイロン": ["device"],
    "ミニアイロン": ["device"],
  };
  const CATEGORY_LABELS = {
    shampoo: "シャンプー",
    treatment: "トリートメント",
    hair_mask: "ヘアマスク・集中ケア",
    leave_in: "洗い流さないトリートメント",
    styling: "スタイリング",
    scalp_care: "頭皮ケア",
    device: "ドライヤー・ヘアアイロン",
  };

  // 洗い流さないトリートメントの形状 → ai2_type
  const FORM_TYPES = {
    oil: ["ヘアオイル", "オイル", "スカルプオイル"],
    milk: ["ヘアミルク", "ミルク", "スタイリングミルク"],
    mist: ["ヘアミスト", "ミスト"],
    cream: ["ヘアクリーム", "バーム", "ヘアバーム", "スタイリングバーム"],
    serum: ["ヘアセラム", "ミックスエッセンス"],
  };
  const FORM_LABELS = { oil: "オイル", milk: "ミルク", mist: "ミスト", cream: "クリーム・バーム", serum: "セラム" };

  // 香り → ai2_scent
  const SCENT_TAGS = {
    citrus: ["シトラス"],
    floral: ["フローラル"],
    fruity: ["フルーティー"],
    woody: ["ウッディ"],
    musk: ["ムスク"],
    soap: ["石けん"],
    herbal: ["ハーブ", "精油"],
    unscented: ["無香料"],
  };
  const SCENT_LABELS = {
    citrus: "シトラス系", floral: "フローラル系", fruity: "フルーティー系", woody: "ウッディ系",
    musk: "ムスク系", soap: "石けん系", herbal: "ハーブ・精油系", unscented: "無香料", subtle: "香りひかえめ",
  };

  // 施術履歴 → ai2_treat（どれか一致で可）
  const TREAT_TAGS = {
    color: ["カラー", "ハイトーンカラー"],
    high_tone: ["ハイトーンカラー", "ブリーチ", "カラー"],
    bleach: ["ブリーチ", "ハイトーンカラー"],
    perm: ["パーマ"],
    straight: ["縮毛矯正"],
  };
  const TREAT_LABELS = { color: "カラー", high_tone: "明るめカラー", bleach: "ブリーチ", perm: "パーマ", straight: "縮毛矯正" };

  // ダメージ段階（ai2_dmg）
  const DMG_LEVEL = { "軽め": 1, "中程度": 2, "強め": 3, "ハイダメージ": 4 };
  const DMG_LABEL = { 1: "軽め", 2: "中程度", 3: "強め", 4: "強め（ハイダメージ）" };

  // くせ → ai2_curl
  const CURL_TAGS = {
    wave: { want: ["うねり", "くせ毛", "伸ばしたい"], conflict: ["巻きたい", "カールヘア"] },
    curly: { want: ["くせ毛", "うねり", "伸ばしたい"], conflict: ["巻きたい", "カールヘア"] },
    strong: { want: ["強いくせ", "くせ毛", "伸ばしたい"], conflict: ["巻きたい", "カールヘア"] },
    perm: { want: ["カールヘア", "巻きたい"], conflict: ["伸ばしたい"] },
  };

  // 翌日の頭皮 → ai2_scalp / ai2_wash / ai2_concern
  const SCALP_TAGS = {
    scalp_oily_evening: { scalp: ["皮脂"], concern: ["頭皮のベタつき"], wash: ["さっぱり", "すっきり", "しっかり", "クレンジング"] },
    scalp_oily_morning: { scalp: ["皮脂"], concern: ["頭皮のベタつき"], wash: ["さっぱり", "すっきり", "クレンジング"] },
    scalp_fresh: { scalp: ["普通", "頭皮環境", "頭皮ケア"], concern: [], wash: ["やさしい"] },
    scalp_dry: { scalp: ["乾燥", "頭皮バリア"], concern: ["頭皮の乾燥"], wash: ["やさしい"] },
    scalp_itchy: { scalp: ["フケ・かゆみ", "敏感", "頭皮バリア"], concern: [], wash: ["やさしい"] },
  };

  // カラーの色味 → ai2_tone
  const TONE_TAGS = {
    tone_yellow: ["紫", "ホワイトブロンド", "ライラック"],
    tone_ash: ["アッシュ"],
    tone_pink: ["ピンク", "サクラ", "ライラック"],
    tone_beige: ["ベージュ", "ブラウン"],
    tone_orange: ["オレンジ", "ブラウン"],
  };

  // 育毛系（医薬部外品）の商品。頭皮の悩みを選んだときだけ候補に入れ、効果は断定しない。
  const SCALP_MEDICATED_CONCERNS = ["薄毛", "抜け毛"];

  /* ---------- 価格 ---------- */
  const PRICE_TIERS = [
    { key: "t1", min: 0, max: 3000 },
    { key: "t2", min: 3000, max: 5000 },
    { key: "t3", min: 5000, max: 8000 },
    { key: "t4", min: 8000, max: Infinity },
  ];
  const BAND_TO_TIER = { under_3000: "t1", "3000_5000": "t2", "5000_8000": "t3" };

  /* ---------- 配点（合計100）---------- */
  const WEIGHTS = {
    concern: 30, finish: 15, hair: 10, curl: 6, treatment: 10, severity: 6,
    scalp: 8, tone: 6, heat: 4, who: 5, preference: 5,
  };
  // 評価できた軸が少ない商品が満点にならないよう、50%に寄せる（重み20相当）
  const PRIOR_WEIGHT = 20;
  // 「同点」とみなす幅（点）。この幅の中では 推し → 利益の目安 → 好み の順で並べる
  const TIE_BAND = 3;

  /* ---------- 小道具 ---------- */
  function has(list) { return Array.isArray(list) && list.length > 0; }
  function any(list, values) { return has(list) && list.some((v) => values.includes(v)); }
  function arr(value) { return Array.isArray(value) ? value : value ? [value] : []; }

  /* ---------- 商品データの整形 ---------- */
  // 入力は collection.hair-check-data.liquid の1行：
  // { id, h, t(title), v(vendor), u(url), img, p(price_min 円), pm(price_max 円), g(利益の目安 0〜3 / null), tags:[ai2_...] }
  function normalizeProduct(raw) {
    const t = {};
    for (const tag of arr(raw.tags)) {
      const m = /^ai2_([a-z_]+):(.+)$/.exec(String(tag));
      if (!m) continue;
      (t[m[1]] = t[m[1]] || []).push(m[2].trim());
    }
    const categories = new Set();
    for (const type of arr(t.type)) for (const c of TYPE_CATEGORY[type] || []) categories.add(c);
    if (any(t.type, ["トリートメント"]) && any(t.routine, ["集中ケア", "スペシャルケア", "週1回", "週1〜2回", "週2回", "週2〜3回"])) {
      categories.add("hair_mask");
    }
    const forms = Object.keys(FORM_TYPES).filter((k) => any(t.type, FORM_TYPES[k]));
    const push = any(t.push, ["フラッグシップ"]) ? 2 : any(t.push, ["推し"]) ? 1 : 0;
    const coverageAxes = ["concern", "finish", "weight", "thick", "soft", "vol", "dmg", "treat", "scent", "curl", "heat", "scalp"];
    const coverage = coverageAxes.filter((axis) => has(t[axis])).length;
    const price = Number(raw.p);
    const priceMax = Number(raw.pm);
    const p = {
      id: raw.id,
      handle: raw.h,
      title: String(raw.t || ""),
      vendor: raw.v || "",
      url: raw.u || (raw.h ? `/products/${raw.h}` : ""),
      image: raw.img || "",
      price: Number.isFinite(price) ? price : null,
      priceMax: Number.isFinite(priceMax) ? priceMax : null,
      profitTier: raw.g === null || raw.g === undefined || raw.g === "" ? null : Number(raw.g),
      t,
      categories: [...categories],
      forms,
      push,
      coverage,
      brandKey: (t.maker && t.maker[0]) || (t.brand && t.brand[0]) || "",
      scalpMedicated: any(t.concern, SCALP_MEDICATED_CONCERNS),
    };
    p.family = familyKey(p);
    p.candidate = p.categories.length > 0 && p.price !== null;
    p.bestEligible = p.coverage >= 2;
    return p;
  }

  // 同じ商品の香り違い・容量違い（別商品として登録されているもの）を1つにまとめるためのキー
  function familyKey(p) {
    const base = p.title
      .normalize("NFKC")
      .replace(/[［[（(【<＜《].*?(］|）|】|>|＞|》|[)]|])/g, " ")
      .replace(/[0-9]+([.][0-9]+)?[ 　]*(ml|g|l|kg|本|個|枚|回分)(?![a-z])/gi, " ")
      .replace(/(詰め?替え?用?|レフィル|refill|リフィル|ポンプ|本体|お試し|トライアル|ミニ|サンプル)/gi, " ")
      .replace(/[ 　]+/g, " ")
      .trim()
      .toLowerCase();
    return `${p.brandKey}|${arr(p.t.line)[0] || ""}|${arr(p.t.type)[0] || ""}|${base}`;
  }

  /* ---------- 回答の整理・矛盾チェック ---------- */
  function selectedCategories(a) {
    const values = arr(a.desired_category);
    if (values.includes("all_recommend")) return [];
    return [...new Set(values.filter((v) => CATEGORY_LABELS[v]))];
  }
  function selectedBrands(a) {
    return [...new Set(arr(a.brand_preference).filter((v) => v && v !== "no_preference"))];
  }
  function selectedTreatments(a) {
    const out = new Set();
    for (const v of arr(a.treatment_history)) {
      if (v === "hair_color" || v === "gray_color") out.add("color");
      if (v === "bright_color") out.add("high_tone");
      if (v === "bleach") out.add("bleach");
      if (v === "perm") out.add("perm");
      if (v === "straightening" || v === "acid_straightening") out.add("straight");
    }
    return [...out];
  }
  function effectiveConcern(a) {
    if (a.primary_concern && a.primary_concern !== "unknown") return a.primary_concern;
    return EXPLORATION[a.concern_detail] || null;
  }

  // 回答どうしで方向が逆のものを見つけ、どちらを優先したかを返す（回答そのものは書き換えない）
  function resolveConflicts(answers) {
    const a = { ...(answers || {}) };
    const notes = [];
    if (a.fragrance_preference && a.fragrance_dislike && a.fragrance_preference === a.fragrance_dislike) {
      notes.push("好きな香りと苦手な香りに同じものが選ばれていたため、苦手な香りの指定は外して比べています。");
      delete a.fragrance_dislike;
    }
    if (["light_silky", "airy"].includes(a.desired_finish) && ["moist", "very_moist"].includes(a.weight_tolerance)) {
      notes.push("「軽い仕上がり」と「しっとり重め」の両方が選ばれていたため、重さは中間として比べています。");
      a.weight_tolerance = "balanced";
    }
    if (effectiveConcern(a) === "low_volume" && a.weight_tolerance === "very_moist") {
      notes.push("ボリューム不足のお悩みと「とてもしっとり」が選ばれていたため、重さは「しっとり」までとして比べています。");
      a.weight_tolerance = "moist";
    }
    if (effectiveConcern(a) === "high_volume" && a.desired_finish === "airy") {
      notes.push("毛量・広がりのお悩みと「ふんわり」が選ばれていたため、仕上がりは軽さ寄りの候補も含めて比べています。");
      a.desired_finish = "light_silky";
    }
    if (a.hair_curl === "none" && effectiveConcern(a) === "wave") {
      // うねりが悩みなのに「ほぼまっすぐ」：くせの質問は使わず、悩みを優先
      notes.push("くせの質問では「ほぼまっすぐ」でしたが、お悩みの「くせ・うねり」を優先しています。");
      a.hair_curl = "unknown";
    }
    return { answers: a, notes };
  }

  /* ---------- 各軸の点数（null＝評価しない）---------- */
  function concernMatch(p, a) {
    const key = effectiveConcern(a);
    if (!key) return null;
    const hit = (k) => any(p.t.concern, CONCERN_TAGS[k] || []) || concernExtras(p, k);
    const hasData = has(p.t.concern) || has(p.t.curl) || has(p.t.vol) || has(p.t.dmg) || has(p.t.scalp);
    if (hit(key)) return 1;
    if ((RELATED_CONCERNS[key] || []).some(hit)) return 0.6;
    return hasData ? 0 : null;
  }

  function finishMatch(p, a) {
    const key = a.desired_finish;
    if (!key || !FINISH_TAGS[key] || !has(p.t.finish)) return null;
    if (any(p.t.finish, FINISH_TAGS[key])) return 1;
    if ((RELATED_FINISHES[key] || []).some((k) => any(p.t.finish, FINISH_TAGS[k]))) return 0.6;
    return 0;
  }

  function hairMatch(p, a) {
    const values = [];
    const th = a.hair_thickness;
    const thick = ["fine", "slightly_fine"].includes(th) ? "細い" : th === "normal" ? "普通" : ["coarse", "slightly_coarse"].includes(th) ? "太い" : null;
    const soft = thick === "細い" ? "柔らかい" : thick === "太い" ? "硬い" : thick === "普通" ? "普通" : null;
    if (thick && has(p.t.thick)) values.push(p.t.thick.includes(thick) ? 1 : 0);
    if (soft && has(p.t.soft)) values.push(p.t.soft.includes(soft) ? 1 : 0);
    const am = a.hair_amount;
    const amount = ["low", "slightly_low"].includes(am) ? "少ない" : ["high", "slightly_high"].includes(am) ? "多い" : null;
    if (amount && has(p.t.vol)) {
      if (p.t.vol.includes(amount)) values.push(1);
      else if (amount === "少ない" && p.t.vol.includes("出したい")) values.push(1);
      else if (amount === "多い" && p.t.vol.includes("出したい")) values.push(0);
      else values.push(0);
    }
    if (!values.length) return null;
    return values.reduce((s, v) => s + v, 0) / values.length;
  }

  function curlMatch(p, a) {
    const rule = CURL_TAGS[a.hair_curl];
    if (!rule || !has(p.t.curl)) return null;
    if (any(p.t.curl, rule.want)) return 1;
    if (p.t.curl.every((v) => rule.conflict.includes(v))) return 0;
    return 0.3;
  }

  function treatmentMatch(p, a) {
    const req = selectedTreatments(a);
    if (!req.length || !has(p.t.treat)) return null;
    const matched = req.filter((r) => any(p.t.treat, TREAT_TAGS[r])).length;
    if (matched === req.length) return 1;
    if (matched > 0) return 0.6;
    return has(p.t.dmg) ? 0.35 : 0;
  }

  // 施術・悩みから、必要なダメージ段階（1〜4）を出す
  function requestedDamageLevel(a) {
    const tr = selectedTreatments(a);
    let level = 0;
    if (tr.includes("color")) level = Math.max(level, 1);
    if (tr.includes("high_tone") || tr.includes("perm")) level = Math.max(level, 2);
    if (tr.includes("bleach")) level = Math.max(level, 3);
    if (tr.includes("straight")) {
      const map = { within_3m: 3, "3_to_6m": 3, "6_to_12m": 2, over_1y: 1 };
      level = Math.max(level, map[a.straightening_recency] || 2);
    }
    if (effectiveConcern(a) === "damage") level = level ? Math.min(4, level + 1) : 2;
    return level || null;
  }

  function severityMatch(p, a) {
    const want = requestedDamageLevel(a);
    if (!want || !has(p.t.dmg)) return null;
    const levels = p.t.dmg.map((v) => DMG_LEVEL[v]).filter(Boolean);
    if (!levels.length) return null;
    const diff = Math.min(...levels.map((l) => Math.abs(l - want)));
    return diff === 0 ? 1 : diff === 1 ? 0.6 : diff === 2 ? 0.2 : 0;
  }

  function scalpMatch(p, a) {
    const rule = SCALP_TAGS[a.scalp_next_day];
    if (!rule) return null;
    const hasData = has(p.t.scalp) || has(p.t.wash) || any(p.t.concern, ["頭皮のベタつき", "頭皮の乾燥"]);
    if (!hasData) return null;
    if (any(p.t.scalp, rule.scalp) || any(p.t.concern, rule.concern) || any(p.t.wash, rule.wash)) return 1;
    return 0;
  }

  function toneMatch(p, a) {
    const tones = TONE_TAGS[a.color_tone];
    if (!tones || !has(p.t.tone)) return null;
    return any(p.t.tone, tones) ? 1 : 0;
  }

  function heatMatch(p, a) {
    if (!["weekly_3_4", "almost_daily"].includes(a.heat_frequency)) return null;
    if (has(p.t.heat) || any(p.t.concern, ["熱ダメージ"])) return 1;
    return null;
  }

  function whoMatch(p, a) {
    const men = any(p.t.for, ["メンズ"]);
    const women = any(p.t.for, ["女性"]);
    if (a.user_type === "female") return men && !women ? 0 : null;
    if (a.user_type === "male") return men ? 1 : women ? 0 : null;
    return null;
  }

  function scentKeysOf(p) {
    return Object.keys(SCENT_TAGS).filter((k) => any(p.t.scent, SCENT_TAGS[k]));
  }

  function scentMatch(p, a) {
    const want = a.fragrance_preference;
    if (!want || want === "no_preference") return null;
    if (want === "subtle") {
      if (any(p.t.scent_lv, ["控えめ"]) || any(p.t.scent, ["無香料"])) return 1;
      if (any(p.t.scent_lv, ["しっかり"])) return 0;
      return null;
    }
    if (!has(p.t.scent)) return null;
    return any(p.t.scent, SCENT_TAGS[want] || []) ? 1 : 0;
  }

  function priceMatch(price, band) {
    if (!Number.isFinite(price) || !band || band === "no_limit" || band === "over_8000_effect_first") return null;
    if (band === "under_3000") return price <= 3000 ? 1 : price <= 5000 ? 0.6 : 0;
    if (band === "3000_5000") return price >= 3000 && price <= 5000 ? 1 : (price >= 2000 && price < 3000) || (price > 5000 && price <= 6500) ? 0.6 : 0;
    if (band === "5000_8000") return price >= 5000 && price <= 8000 ? 1 : (price >= 3500 && price < 5000) || (price > 8000 && price <= 10000) ? 0.6 : 0;
    return null;
  }

  function weightMatch(p, a) {
    const target = { very_light: 1, slightly_light: 1.5, balanced: 2, moist: 2.5, very_moist: 3 }[a.weight_tolerance];
    if (!target || !has(p.t.weight)) return null;
    const levels = p.t.weight.map((v) => ({ "軽い": 1, "中間": 2, "重い": 3 }[v])).filter(Boolean);
    if (!levels.length) return null;
    const diff = Math.min(...levels.map((l) => Math.abs(l - target)));
    return diff === 0 ? 1 : diff <= 0.5 ? 0.75 : diff <= 1 ? 0.4 : diff <= 1.5 ? 0.15 : 0;
  }

  function preferenceMatch(p, a, axis) {
    const priority = a.selection_priority;
    if (priority === "fragrance") return scentMatch(p, a);
    if (priority === "price") return priceMatch(p.price, a.price_band);
    if (priority === "texture") return weightMatch(p, a);
    if (priority === "effect") {
      const values = [axis.concern, axis.treatment].filter((v) => v !== null);
      return values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;
    }
    // こだわらない：重さ → 価格 の順に、評価できるものを使う
    const w = weightMatch(p, a);
    return w !== null ? w : priceMatch(p.price, a.price_band);
  }

  /* ---------- 除外（必須条件）---------- */
  function activeTier(a) {
    const key = a.price_tier || BAND_TO_TIER[a.price_band];
    return PRICE_TIERS.find((t) => t.key === key) || null;
  }

  function hardExclusion(p, a, options) {
    if (!p.candidate) return "種類・価格の情報が不足";
    const categories = selectedCategories(a);
    if (categories.length) {
      if (!categories.some((c) => p.categories.includes(c))) return "希望のアイテムと違う";
    } else if (p.categories.every((c) => c === "device")) {
      return "美容機器は選んだときだけ表示";
    }
    const brands = selectedBrands(a);
    if (brands.length && !brands.includes(p.brandKey)) return "希望のブランドと違う";
    const form = a.leave_in_form;
    if (categories.length === 1 && categories[0] === "leave_in" && form && FORM_TYPES[form] && p.forms.length && !p.forms.includes(form)) {
      return "希望の形状と違う";
    }
    if (!(options && options.ignorePriceBand)) {
      const tier = activeTier(a);
      if (tier && Number.isFinite(p.price)) {
        if (p.price < tier.min) return "希望の価格帯より安い";
        if (p.price > tier.max) return "希望の価格帯より高い";
      }
    }
    const dislike = a.fragrance_dislike;
    if (dislike && SCENT_TAGS[dislike] && any(p.t.scent, SCENT_TAGS[dislike])) return "苦手な香り";
    // 育毛系（医薬部外品）は、頭皮の悩み・頭皮ケアを選んだときだけ
    if (p.scalpMedicated && effectiveConcern(a) !== "scalp" && !categories.includes("scalp_care")) {
      return "頭皮ケアを選んだときだけ表示";
    }
    return null;
  }

  /* ---------- 理由（効果は断定せず、商品説明の登録内容だけを示す）---------- */
  function buildReasons(p, a, axis) {
    const c = [];
    const key = effectiveConcern(a);
    const label = key === "scalp" || p.scalpMedicated ? "頭皮ケア" : CONCERN_LABELS[key];
    if (axis.concern === 1) c.push({ v: 100, text: `「${label}」が気になる方向けの商品` });
    else if (axis.concern === 0.6) c.push({ v: 60, text: `「${label}」に近いお悩み向けの商品` });
    if (axis.treatment === 1) {
      const names = selectedTreatments(a).map((r) => TREAT_LABELS[r]).join("・");
      c.push({ v: 95, text: `${names}をした髪向けの記載あり` });
    } else if (axis.treatment === 0.6) c.push({ v: 55, text: "施術をした髪向けの記載あり" });
    if (axis.severity === 1 || axis.severity === 0.6) {
      const lv = p.t.dmg.map((v) => DMG_LEVEL[v]).filter(Boolean);
      const want = requestedDamageLevel(a);
      const nearest = lv.sort((x, y) => Math.abs(x - want) - Math.abs(y - want))[0];
      c.push({ v: axis.severity === 1 ? 93 : 52, text: `ダメージ「${DMG_LABEL[nearest]}」向け` });
    }
    if (axis.finish === 1) c.push({ v: 90, text: `仕上がり：${FINISH_LABELS[a.desired_finish]}` });
    else if (axis.finish === 0.6) c.push({ v: 50, text: "ご希望に近い仕上がり" });
    if (axis.hair === 1) c.push({ v: 85, text: "髪の太さ・量に合う記載あり" });
    if (axis.curl === 1) c.push({ v: 84, text: a.hair_curl === "perm" ? "パーマ・巻き髪向けの記載あり" : "くせ・うねりのある髪向けの記載あり" });
    if (axis.scalp === 1) c.push({ v: 83, text: "翌日の頭皮の状態に合う洗い心地・タイプ" });
    if (axis.tone === 1) c.push({ v: 82, text: "ご希望の色味のカラーケア" });
    if (axis.heat === 1) c.push({ v: 80, text: "アイロン・ドライヤー前に使える記載あり" });
    if (axis.who === 1) c.push({ v: 78, text: "メンズ向け" });
    const scent = a.fragrance_preference;
    if (scent && scent !== "no_preference" && scentMatch(p, a) === 1) c.push({ v: 70, text: `香り：${SCENT_LABELS[scent]}` });
    if (priceMatch(p.price, a.price_band) === 1) c.push({ v: 64, text: "ご希望の価格帯" });
    if (weightMatch(p, a) === 1) c.push({ v: 62, text: "ご希望の重さに近い使用感" });
    if (selectedBrands(a).length && p.brandKey) c.push({ v: 47, text: `選んだブランド（${p.brandKey}）` });
    const categories = selectedCategories(a);
    if (categories.length) {
      const hit = categories.filter((x) => p.categories.includes(x)).map((x) => CATEGORY_LABELS[x]);
      if (hit.length) c.push({ v: 45, text: `探しているアイテム（${hit.join("・")}）` });
    }
    if (!c.length) c.push({ v: 1, text: "商品説明の登録内容から選んでいます" });
    return c.sort((x, y) => y.v - x.v).slice(0, 3).map((x) => x.text);
  }

  /* ---------- 点数 ---------- */
  function scoreProduct(p, a, options) {
    const excludedReason = hardExclusion(p, a, options);
    if (excludedReason) return { product: p, excluded: true, excludedReason };
    const axis = {
      concern: concernMatch(p, a),
      finish: finishMatch(p, a),
      hair: hairMatch(p, a),
      curl: curlMatch(p, a),
      treatment: treatmentMatch(p, a),
      severity: severityMatch(p, a),
      scalp: scalpMatch(p, a),
      tone: toneMatch(p, a),
      heat: heatMatch(p, a),
      who: whoMatch(p, a),
      preference: null,
    };
    axis.preference = preferenceMatch(p, a, axis);
    let earned = 0;
    let evaluated = 0;
    for (const [k, w] of Object.entries(WEIGHTS)) {
      if (axis[k] === null || axis[k] === undefined) continue;
      earned += w * axis[k];
      evaluated += w;
    }
    const score = Math.round(((earned + 0.5 * PRIOR_WEIGHT) / (evaluated + PRIOR_WEIGHT)) * 1000) / 10;
    const coreScore = [axis.concern, axis.treatment, axis.severity].filter((v) => v !== null).reduce((s, v) => s + v, 0);
    const preferenceScore = [axis.preference, axis.finish].filter((v) => v !== null).reduce((s, v) => s + v, 0);
    return {
      product: p,
      excluded: false,
      score,
      earned: Math.round(earned),
      evaluatedWeight: evaluated,
      axis,
      coreScore,
      preferenceScore,
      reasons: buildReasons(p, a, axis),
    };
  }

  function compareScored(x, y) {
    const tx = x.product.bestEligible ? 1 : 0;
    const ty = y.product.bestEligible ? 1 : 0;
    if (tx !== ty) return ty - tx;
    const bx = Math.round(x.score / TIE_BAND);
    const by = Math.round(y.score / TIE_BAND);
    if (bx !== by) return by - bx;
    // ほぼ同点：推し → 利益の目安（不明は中間）→ 好みの一致 → 情報の埋まり具合 → 点数
    if (x.product.push !== y.product.push) return y.product.push - x.product.push;
    const gx = x.product.profitTier === null ? 1.5 : x.product.profitTier;
    const gy = y.product.profitTier === null ? 1.5 : y.product.profitTier;
    if (gx !== gy) return gy - gx;
    if (x.preferenceScore !== y.preferenceScore) return y.preferenceScore - x.preferenceScore;
    if (x.product.coverage !== y.product.coverage) return y.product.coverage - x.product.coverage;
    if (x.score !== y.score) return y.score - x.score;
    return x.product.title.localeCompare(y.product.title, "ja");
  }

  function recommend(products, answers, options) {
    const limit = Number(options && options.limit) || 3;
    const scored = products.map((p) => scoreProduct(p, answers, options)).filter((s) => !s.excluded).sort(compareScored);
    const unique = [];
    const families = new Set();
    for (const s of scored) {
      if (families.has(s.product.family)) continue;
      families.add(s.product.family);
      unique.push(s);
    }
    if (!unique.length) return [];
    const selected = [];
    const take = (s, role) => {
      if (!s || selected.some((e) => e.product.handle === s.product.handle)) return;
      selected.push({ ...s, role: s.product.bestEligible ? role : "条件に合う候補" });
    };
    const rest = () => unique.filter((s) => !selected.some((e) => e.product.handle === s.product.handle));
    take(unique[0], "いちばん近い候補");
    take(rest().sort((x, y) => y.coreScore - x.coreScore || compareScored(x, y))[0], "お悩みへの合い方を優先");
    take(rest().sort((x, y) => y.preferenceScore - x.preferenceScore || compareScored(x, y))[0], "ご希望の条件を優先");
    for (const s of unique) {
      if (selected.length >= limit) break;
      take(s, "比べてみる候補");
    }
    return selected.slice(0, limit);
  }

  function priceTierSummary(products, a) {
    const base = PRICE_TIERS.find((t) => t.key === BAND_TO_TIER[a.price_band]);
    if (!base) return [];
    const current = activeTier(a) || base;
    const i = PRICE_TIERS.indexOf(base);
    const wanted = [
      { key: PRICE_TIERS[i - 1] && PRICE_TIERS[i - 1].key, label: "もう少し手頃に" },
      { key: base.key, label: "ご希望の価格帯" },
      { key: PRICE_TIERS[i + 1] && PRICE_TIERS[i + 1].key, label: "ワンランク上" },
      { key: PRICE_TIERS[i + 2] && PRICE_TIERS[i + 2].key, label: "さらに上" },
    ].filter((e) => e.key);
    const results = wanted.map((e) => {
      const top = recommend(products, { ...a, price_tier: e.key }, { limit: 1 })[0];
      return { key: e.key, label: e.label, active: e.key === current.key, price: top ? top.product.price : null, available: Boolean(top) };
    });
    const anchor = results.find((r) => r.active);
    const anchorPrice = anchor && Number.isFinite(anchor.price) ? anchor.price : null;
    for (const r of results) r.delta = anchorPrice !== null && Number.isFinite(r.price) && !r.active ? r.price - anchorPrice : null;
    return results;
  }

  function recommendWithFallback(products, answers, options) {
    const resolved = resolveConflicts(answers);
    const source = resolved.answers;
    const attempts = [{ level: 0, message: "", answers: source }];
    const categories = selectedCategories(source);
    if (categories.length === 1 && categories[0] === "leave_in" && source.leave_in_form && source.leave_in_form !== "no_preference") {
      attempts.push({ level: 1, message: "形状の指定を広げて候補を表示しています。", answers: { ...source, leave_in_form: "no_preference" } });
    }
    if (selectedBrands(source).length) {
      attempts.slice().forEach((at) => attempts.push({
        level: 2,
        message: "選んだブランドには条件に合う商品がなかったため、ほかのブランドからご提案しています。",
        answers: { ...at.answers, brand_preference: ["no_preference"] },
      }));
    }
    for (const at of attempts) {
      const items = recommend(products, at.answers, options);
      if (items.length) {
        return { items, priceTiers: priceTierSummary(products, at.answers), message: at.message, conflicts: resolved.notes, effectiveAnswers: at.answers };
      }
    }
    const tiers = priceTierSummary(products, source);
    const priceOnly = Boolean(activeTier(source) && tiers.some((t) => !t.active && t.available));
    return {
      items: [],
      priceTiers: tiers,
      message: priceOnly
        ? "ご希望の価格帯では条件に合う商品が見つかりませんでした。下の価格帯からお選びいただけます。"
        : "条件に合う商品が見つかりませんでした。",
      conflicts: resolved.notes,
      effectiveAnswers: source,
    };
  }

  // 「使いたいブランド」の選択肢（選んだアイテムに合う商品があるブランドだけ）
  function brandOptions(products, a) {
    const categories = selectedCategories(a || {});
    const found = new Map();
    for (const p of products) {
      if (!p.brandKey || !p.candidate) continue;
      if (categories.length ? !categories.some((c) => p.categories.includes(c)) : p.categories.every((c) => c === "device")) continue;
      const e = found.get(p.brandKey) || { key: p.brandKey, label: p.brandKey, count: 0 };
      e.count += 1;
      found.set(p.brandKey, e);
    }
    return [...found.values()].sort((x, y) => y.count - x.count || x.label.localeCompare(y.label, "ja"));
  }

  // その回の内訳（ページ内の window.__hairCheckTrace でだけ確認できる。外部送信なし）
  function explain(products, answers) {
    const a = resolveConflicts(answers).answers;
    const excludedByReason = {};
    const considered = [];
    for (const p of products) {
      const s = scoreProduct(p, a);
      if (s.excluded) { excludedByReason[s.excludedReason] = (excludedByReason[s.excludedReason] || 0) + 1; continue; }
      considered.push({ handle: p.handle, title: p.title, score: s.score, push: p.push, g: p.profitTier, axis: s.axis });
    }
    considered.sort((x, y) => y.score - x.score);
    return { total: products.length, considered: considered.length, excludedByReason, top: considered.slice(0, 20) };
  }

  return {
    normalizeProduct,
    recommend,
    recommendWithFallback,
    priceTierSummary,
    brandOptions,
    resolveConflicts,
    scoreProduct,
    explain,
    selectedCategories,
    selectedTreatments,
    effectiveConcern,
    CATEGORY_LABELS,
    FINISH_LABELS,
    CONCERN_LABELS,
    SCENT_LABELS,
    FORM_LABELS,
    WEIGHTS,
  };
});
